import { roundTo } from "./tillHelpers";
import { logIntegrityViolation } from "./dataIntegrity";

/**
 * BUG-2592 (GPR SHDPOS/23201, 23203): a cart line whose weight was cleared to 0 keeps its old
 * nettotal / linegross. The cart totals already leave it out (cart.js isLiveCartLine), so the
 * header (orderTotal) is short by exactly that amount, while the order builder still sends the
 * line with its amount. posOrderProcessor1 / confirmPOSLayawayOrder1 compare orderTotal with
 * sum(linegross) + roundoff and refuse the order, which then stays as a pending bill although
 * the customer already has the invoice.
 *
 * The weight-0 skip in the regular builder stays commented out on purpose (BUG-2307: a cancelled
 * order zeroes every line and must still send them), so the lines are kept and only their
 * amounts are zeroed -- and only when the header already leaves those amounts out:
 *
 *   headerGross = orderTotal - roundoff - charges - tcs
 *   act only if |headerGross - sumLineGross| > tolerance
 *           and |headerGross - (sumLineGross - ghostGross)| <= tolerance
 *
 * Every other order is returned untouched: OFC "B & M" orders whose qty-0 lines ARE in the
 * header, cancelled orders with Rs 0 lines, return lines, orders whose header and lines already
 * agree, and any shape the rule cannot explain (no change = the old behaviour).
 *
 * @param {Object} orderJson - the outbound payload (mutated in place when it acts)
 * @param {Object} [opts]
 * @param {number} [opts.charges=0] - serviceCharge + tip + deiveryCharge + packingCharge + others
 * @param {number} [opts.tcs=0] - TCS amount on the order
 * @param {string} [opts.context=""]
 * @param {Object} [opts.db=null] - db adapter for the durable sequenceAuditLog
 * @param {number} [opts.tolerance=0.05]
 * @returns {{ changed: boolean, lines: number[] }} line numbers (orderJson.line[].line) zeroed
 */
export function zeroExcludedQtyZeroLines(orderJson, { charges = 0, tcs = 0, context = "", db = null, tolerance = 0.05 } = {}) {
  const lines = Array.isArray(orderJson?.line) ? orderJson.line : [];
  const isGhost = (l) => l && !l.returnline && (Number(l.qty) || 0) === 0 && Math.abs(Number(l.linegross) || 0) > 0;
  const ghosts = lines.filter(isGhost);
  if (ghosts.length === 0) return { changed: false, lines: [] };

  const sumLineGross = lines.reduce((s, l) => s + (Number(l?.linegross) || 0), 0);
  const ghostGross = ghosts.reduce((s, l) => s + (Number(l.linegross) || 0), 0);
  const headerGross = (Number(orderJson.orderTotal) || 0) - (Number(orderJson.roundoff) || 0) - (Number(charges) || 0) - (Number(tcs) || 0);
  const diffAsSent = Math.abs(headerGross - sumLineGross);
  const diffWithout = Math.abs(headerGross - (sumLineGross - ghostGross));

  if (!(diffAsSent > tolerance && diffWithout <= tolerance)) return { changed: false, lines: [] };

  const zeroed = [];
  for (const l of ghosts) {
    zeroed.push({ line: l.line, mProductId: l.mProductId, linegross: l.linegross, linenet: l.linenet, linetax: l.linetax });
    l.linegross = 0;
    l.linenet = 0;
    l.linetax = 0;
    l.linestd = 0;
    if (Array.isArray(l.tax)) {
      for (const t of l.tax) {
        if (!t) continue;
        t.taxAmt = 0;
        t.taxableAmt = 0;
      }
    }
  }
  logIntegrityViolation(
    "DI-38",
    {
      context,
      documentno: orderJson.documentno,
      sOrderID: orderJson.sOrderID,
      headerGross: roundTo(headerGross, 4),
      sumLineGrossAsBuilt: roundTo(sumLineGross, 4),
      ghostGross: roundTo(ghostGross, 4),
      zeroed,
    },
    db
  );
  return { changed: true, lines: zeroed.map((z) => z.line) };
}
